
/**
 * Parse the response from axios requests.  Validates the response
 * and returns the result if valid.  If the request is not 'OK' then
 * an error response will be returned.
 * 
 * @param {Object} result
 * @return {Object} 
 */
export const parseResponse = (result) => {

    // _parsed flag prevents this function from being executed
    // multiple times. (i.e. passing through hook and service)
    if (result && result._parsed) {
        return result;
    }
    
    const { status } = result;
    if (status >= 200 && status < 300) {
        result._parsed = true;
        return result;
    }

    return parseError({ response: result });
}

const SAFE_RESPONSE_HEADERS = new Set([
    'content-type', 'content-length', 'retry-after', 'cache-control', 'expires',
    'date', 'etag', 'last-modified', 'ratelimit-limit', 'ratelimit-remaining',
    'ratelimit-reset', 'x-ratelimit-limit', 'x-ratelimit-remaining', 'x-ratelimit-reset',
]);

function safeResponseHeaders(headers) {
    if (!headers || typeof headers !== 'object') return undefined;
    const safe = {};
    Object.entries(headers).forEach(([name, value]) => {
        const key = name.toLowerCase();
        if (SAFE_RESPONSE_HEADERS.has(key) && (typeof value === 'string' || typeof value === 'number')) safe[key] = value;
    });
    return safe;
}

/**
 * Parse the error of an axios request. Validates a correct
 * error response was received without logging upstream messages.
 * A generic response will be returned if a server error was
 * recieved.
 * 
 * Returns an object that matches axios `error.response`.
 * 
 * @param {Object} error
 * @return {Object}
 */
export const parseError = (error) => {

    //console.log("API ERROR", error);

    // _parsed flag prevents this function from being executed
    // multiple times. (i.e. passing through hook and service)
    if (error && error._parsed) {
        return error;
    }

    const response = error?.response;
    const code = typeof error?.code === 'string' ? error.code : undefined;
    if (!response?.status) {
        return {
            _parsed: true,
            status: 500,
            statusText: 'Error',
            data: { message: 'An error occured.' },
            ...(code ? { code } : {}),
            transport: code === 'ERR_CANCELED' ? 'cancelled' :
                (code === 'ECONNABORTED' || code === 'ETIMEDOUT') ? 'timeout' :
                (code === 'ERR_NETWORK' || (error?.isAxiosError && !code)) ? 'network' : 'other',
        };
    }
    const headers = safeResponseHeaders(response.headers);
    const retryAfter = headers?.['retry-after'];
    const data = response.status >= 500
        ? { message: 'Oh no. Looks like our server did something wrong.' }
        : response.data == null || response.data === ''
            ? { message: 'An error occured.' }
            : typeof response.data === 'object' && !Array.isArray(response.data)
                ? { ...response.data }
                : response.data;
    if (data && typeof data === 'object' && !Array.isArray(data) && data._message) data.message = data._message;
    return {
        _parsed: true,
        status: response.status,
        statusText: response.statusText,
        data,
        ...(code === 'ERR_CANCELED' ? { code, transport: 'cancelled' } : {}),
        ...(headers ? { headers } : {}),
        ...(typeof retryAfter === 'string' || typeof retryAfter === 'number'
            ? { retryAfter: String(retryAfter) } : {}),
    };
}
